'use client';

import { c as _c } from "react/compiler-runtime";
import { useI18n } from '~/features/i18n';
import { I18n_AmountPerParticipant } from '~/features/i18n/translations';
import { useVATRate } from '~/features/portals/money';
import { Units } from '~/modules/units';
import { Car, Clock, Home, Map, MapPin, Monitor, Sun, Users, type LucideIcon } from 'lucide-react';
import type { ActivityLocationType } from '@funkey/constants';
import { formatDurationRange } from '~/features/activities/search/shared';
import { cx } from 'class-variance-authority';
import Tooltip from '~/components/primitives/Tooltip';
export const ActivityCardPriceDescription: React.FC<Parameters<typeof Units.formatPriceRangeEUR>[1] & {
  className?: string;
}> = activity => {
  const $ = _c(7);
  const vat = useVATRate();
  let t0;
  if ($[0] !== activity.maxPrice || $[1] !== activity.minPrice || $[2] !== vat) {
    t0 = <I18n_AmountPerParticipant amount={i18n => Units.formatPriceRangeEUR(i18n, {
      maxPrice: activity.maxPrice ? vat.calcEUR(activity.maxPrice) : null,
      minPrice: activity.minPrice ? vat.calcEUR(activity.minPrice) : null
    })} disableEURPrefix={true} />;
    $[0] = activity.maxPrice;
    $[1] = activity.minPrice;
    $[2] = vat;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== activity.className || $[5] !== t0) {
    t1 = <div className={activity.className}>{t0}</div>;
    $[4] = activity.className;
    $[5] = t0;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
};

/** The fact rows under the description: location, duration, group size, indoor
 * or outdoor, and the route to the partner. Every value belongs to THIS
 * activity — a card stands for one partner, never for its whole group. */
export const ActivityCardFacts: React.FC<{
  minParticipants: number;
  maxParticipants: number | null;
  durationMinutes: {
    min: number;
    max: number;
  } | null;
  locationType: readonly ActivityLocationType[] | undefined;
  comesToUs: boolean;
  canGoTo: boolean;
  fixedDistanceKm: number | null;
  /** The clean look renders the route in its own band under the image. */
  hideRoute?: boolean;
}> = props => {
  const $ = _c(35);
  const i18n = useI18n();
  let t0;
  if ($[0] !== i18n) {
    t0 = function renderDuration(duration) {
      const formatted = formatDurationRange(i18n.locale, duration);
      const isRange = duration.min !== duration.max;
      const mixed = parts => parts.hours === 0 ? i18n._("activity.card.durationMinutes", {
        value: parts.minutes
      }, {
        message: "{value} min"
      }) : i18n._("activity.card.durationHoursMinutes", parts, {
        message: "{hours}h {minutes}m"
      });
      if (formatted.unit === "mixed") {
        return isRange ? i18n._("activity.card.durationRangeMixed", {
          min: mixed(formatted.minParts),
          max: mixed(formatted.maxParts)
        }, {
          message: "{min}-{max}"
        }) : mixed(formatted.minParts);
      }
      if (formatted.unit === "minutes") {
        return isRange ? i18n._("activity.card.durationRangeMinutes", formatted, {
          message: "{min}-{max} min"
        }) : i18n._("activity.card.durationMinutes", {
          value: formatted.min
        }, {
          message: "{value} min"
        });
      }
      return isRange ? i18n._("activity.card.durationRangeHours", formatted, {
        message: "{min}-{max} hours"
      }) : i18n._("activity.card.durationHours", {
        value: formatted.min
      }, {
        message: "{value} hours"
      });
    };
    $[0] = i18n;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const renderDuration = t0;
  let t1;
  let t2;
  let t3;
  if ($[2] !== i18n) {
    t1 = () => i18n._("activity.locationType.indoor", undefined, {
      message: "Indoor"
    });
    t2 = () => i18n._("activity.locationType.outdoor", undefined, {
      message: "Outdoor"
    });
    t3 = () => i18n._("activity.locationType.virtual", undefined, {
      message: "Online"
    });
    $[2] = i18n;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
  }
  let locationTypes;
  let t4;
  if ($[6] !== props.locationType || $[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    const locationTypeLabel = {
      INDOOR: t1,
      OUTDOOR: t2,
      VIRTUAL: t3
    };
    let t5;
    if ($[12] !== props.locationType) {
      t5 = type => props.locationType?.includes(type);
      $[12] = props.locationType;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    locationTypes = LOCATION_TYPE_ORDER.filter(t5);
    t4 = locationTypes.map((type_0, index) => {
      const label = locationTypeLabel[type_0]();
      return index === 0 ? label : label.toLocaleLowerCase();
    }).join(", ");
    $[6] = props.locationType;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = locationTypes;
    $[11] = t4;
  } else {
    locationTypes = $[10];
    t4 = $[11];
  }
  const locationTypeText = t4;
  let t5;
  if ($[14] !== props.durationMinutes || $[15] !== renderDuration) {
    t5 = props.durationMinutes ? <ActivityCardFact icon={Clock}>{renderDuration(props.durationMinutes)}</ActivityCardFact> : <li aria-hidden={true} />;
    $[14] = props.durationMinutes;
    $[15] = renderDuration;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== i18n || $[18] !== props.maxParticipants || $[19] !== props.minParticipants) {
    t6 = props.maxParticipants ? i18n._("activity.card.participantRange", {
      min: props.minParticipants,
      max: props.maxParticipants
    }) : i18n._("activity.card.participantsFrom", {
      min: props.minParticipants
    });
    $[17] = i18n;
    $[18] = props.maxParticipants;
    $[19] = props.minParticipants;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t6) {
    t7 = <ActivityCardFact icon={Users}>{t6}</ActivityCardFact>;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== locationTypeText || $[24] !== locationTypes[0]) {
    t8 = locationTypes[0] ? <ActivityCardFact icon={LOCATION_TYPE_ICONS[locationTypes[0]]}>{locationTypeText}</ActivityCardFact> : <li aria-hidden={true} />;
    $[23] = locationTypeText;
    $[24] = locationTypes[0];
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t5 || $[27] !== t7 || $[28] !== t8) {
    t9 = <ul className="grid grid-cols-2 gap-x-3 gap-y-2">{t5}{t7}{t8}</ul>;
    $[26] = t5;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== props) {
    t10 = !props.hideRoute && <ActivityCardRoute {...props} />;
    $[30] = props;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== t10 || $[33] !== t9) {
    t11 = <div className="flex flex-col gap-3 text-sm font-medium text-primary">{t9}{t10}</div>;
    $[32] = t10;
    $[33] = t9;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  return t11;
};

/** The route stands apart: it is the one fact a visitor compares between
 * cards. Both options are always there; the one that does not apply is faded
 * and struck through, with the reason in a tooltip. `band` is the clean look:
 * the distance in bold, the label muted. */
export const ActivityCardRoute: React.FC<{
  comesToUs: boolean;
  canGoTo: boolean;
  fixedDistanceKm: number | null;
  band?: boolean;
}> = props => {
  const $ = _c(29);
  const i18n = useI18n();
  const hasDistance = props.canGoTo && props.fixedDistanceKm != null;
  let t0;
  if ($[0] !== i18n) {
    t0 = i18n._("activity.filter.location.option.fixed", undefined, {
      message: "I go to the partner"
    });
    $[0] = i18n;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const toPartnerLabel = t0;
  const t1 = props.band && "text-base";
  let t2;
  if ($[2] !== t1) {
    t2 = cx("grid grid-cols-2 gap-x-3 gap-y-2 text-sm font-medium text-primary", t1);
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== i18n || $[5] !== props.comesToUs) {
    t3 = props.comesToUs ? undefined : i18n._("activity.card.route.noCustomLocation", undefined, {
      message: "This activity does not travel to your location"
    });
    $[4] = i18n;
    $[5] = props.comesToUs;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== i18n) {
    t4 = i18n._("activity.filter.location.option.custom", undefined, {
      message: "Location of choice"
    });
    $[7] = i18n;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <ActivityCardFact accent={true} icon={MapPin} mutedReason={t3}>{t4}</ActivityCardFact>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = props.band ? Map : Car;
  let t7;
  if ($[12] !== i18n || $[13] !== props.canGoTo) {
    t7 = props.canGoTo ? undefined : i18n._("activity.card.route.noFixedLocation", undefined, {
      message: "This activity has no fixed location to visit"
    });
    $[12] = i18n;
    $[13] = props.canGoTo;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== hasDistance || $[16] !== i18n || $[17] !== props.band || $[18] !== props.fixedDistanceKm || $[19] !== toPartnerLabel) {
    t8 = props.band && hasDistance && props.fixedDistanceKm != null ? <><strong className="font-bold">{Math.round(props.fixedDistanceKm)}km</strong>{" "}<span className="font-normal text-blue-500">{i18n._("activity.card.route.toPartner", undefined, {
          message: "to the partner"
        })}</span></> : hasDistance && props.fixedDistanceKm != null ? i18n._("activity.card.atPartnerDistance", {
      km: Math.round(props.fixedDistanceKm)
    }) : toPartnerLabel;
    $[15] = hasDistance;
    $[16] = i18n;
    $[17] = props.band;
    $[18] = props.fixedDistanceKm;
    $[19] = toPartnerLabel;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t6 || $[22] !== t7 || $[23] !== t8) {
    t9 = <ActivityCardFact accent={true} icon={t6} mutedReason={t7}>{t8}</ActivityCardFact>;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t2 || $[26] !== t5 || $[27] !== t9) {
    t10 = <ul className={t2}>{t5}{t9}</ul>;
    $[25] = t2;
    $[26] = t5;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  return t10;
};
const LOCATION_TYPE_ORDER = ['INDOOR', 'OUTDOOR', 'VIRTUAL'] as const satisfies readonly ActivityLocationType[];
const LOCATION_TYPE_ICONS = {
  INDOOR: Home,
  OUTDOOR: Sun,
  VIRTUAL: Monitor
} as const satisfies Record<ActivityLocationType, LucideIcon>;

/** A fact that does not apply stays in its slot, struck through and faded,
 * with the reason in a tooltip. */
const ActivityCardFact: React.FC<React.PropsWithChildren<{
  icon: LucideIcon;
  mutedReason?: string;
  accent?: boolean;
}>> = props => {
  const $ = _c(19);
  const t0 = props.mutedReason && "opacity-40";
  let t1;
  if ($[0] !== t0) {
    t1 = cx("flex min-w-0 items-center gap-2", t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const T0 = props.icon;
  const t2 = props.accent ? "text-tertiary" : "text-blue-400";
  let t3;
  if ($[2] !== t2) {
    t3 = cx("size-4 shrink-0", t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== props.icon || $[5] !== t3) {
    t4 = <T0 className={t3} strokeWidth={1.75} aria-hidden={true} />;
    $[4] = props.icon;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = props.mutedReason && "line-through";
  let t6;
  if ($[7] !== t5) {
    t6 = cx("truncate", t5);
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== props.children || $[10] !== t6) {
    t7 = <span className={t6}>{props.children}</span>;
    $[9] = props.children;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t1 || $[13] !== t4 || $[14] !== t7) {
    t8 = <span className={t1}>{t4}{t7}</span>;
    $[12] = t1;
    $[13] = t4;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const fact = t8;
  let t9;
  if ($[16] !== fact || $[17] !== props.mutedReason) {
    t9 = <li className="min-w-0">{props.mutedReason ? <><span className="sr-only">{props.mutedReason}</span><Tooltip as="div" content={props.mutedReason} className="max-w-56">{fact}</Tooltip></> : fact}</li>;
    $[16] = fact;
    $[17] = props.mutedReason;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
};

/** The line the reworked card shows under the facts: the one written for the
 * card when `activities:card-descriptions` has produced it, the partner's own
 * summary until then. Trimmed, because a stored value of whitespace should fall
 * through rather than render as an empty paragraph.
 *
 * The control card never calls this — it keeps showing `shortDescription`, so
 * the experiment compares layout rather than copy. */
export function resolveCardDescription(input: {
  cardDescription?: string | null;
  shortDescription?: string | null;
}): string {
  return input.cardDescription?.trim() || input.shortDescription?.trim() || '';
}